import React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../utils/cn'
import { disabledFieldProps } from './input'
import { Spinner } from './spinner'

const iconButtonVariants = cva(
  'inline-flex items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-surface-canvas aria-disabled:pointer-events-none aria-disabled:opacity-50',
  {
    variants: {
      variant: {
        solid: 'bg-primary text-primary-foreground hover:bg-primary-hover',
        outline:
          'border border-line-emphasized bg-transparent text-content-primary hover:bg-surface-subtle',
        ghost:
          'bg-transparent text-content-secondary hover:bg-surface-subtle hover:text-content-primary',
        subtle: 'bg-surface-subtle text-content-primary hover:bg-surface-muted',
      },
      size: {
        xs: 'h-7 w-7 text-xs',
        sm: 'h-8 w-8 text-sm',
        md: 'h-10 w-10 text-base',
        lg: 'h-12 w-12 text-lg',
      },
    },
    defaultVariants: { variant: 'ghost', size: 'sm' },
  },
)

export interface IconButtonProps
  extends
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>,
    VariantProps<typeof iconButtonVariants> {
  'aria-label': string
  icon?: React.ReactNode
  isLoading?: boolean
  isDisabled?: boolean
  isActive?: boolean
  isRound?: boolean
  colorScheme?: string
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      className,
      variant,
      size,
      icon,
      isLoading,
      isDisabled,
      isActive,
      isRound,
      disabled,
      colorScheme: _cs,
      type,
      children,
      ...props
    },
    ref,
  ) => (
    <button
      ref={ref}
      // Match Button's default: a native <button> defaults to type="submit", but
      // the codebase relies on a type="button" default, so an IconButton inside a
      // <form> must not submit unless it opts in.
      type={type ?? 'button'}
      className={cn(
        iconButtonVariants({ variant, size }),
        isRound && 'rounded-full',
        isActive && 'bg-surface-muted',
        className,
      )}
      aria-busy={isLoading || undefined}
      {...props}
      // Disabled via JS/CSS (aria-disabled + guards), not the native attribute.
      {...disabledFieldProps(disabled || isDisabled || isLoading)}
    >
      {isLoading ? <Spinner size="sm" className="text-current" /> : (icon ?? children)}
    </button>
  ),
)
IconButton.displayName = 'IconButton'
